import type {
  ChangePageV1,
  ClientPageV1,
  ConflictV1,
  CommandRequestV1,
  CommittedEventV1,
  DocumentPageV1,
  EnvelopeV1,
  FileTransferV1,
  PrepareDocumentUploadV1,
  PrepareUploadMaterialV1,
  QueryRequestV1,
  ResponseV1,
  SnapshotControlV1,
  SnapshotEntityV1,
  SnapshotPageV1,
  StableErrorV1,
} from './generated/mlo/foundation/v1/foundation_pb.js';
import {
  EntityTypeV1,
  EventTypeV1,
  ErrorCodeV1,
} from './generated/mlo/foundation/v1/foundation_pb.js';

export type WireValidationCode =
  | 'MISSING_ONEOF'
  | 'INVALID_FIELD'
  | 'INVALID_CURSOR_STATE'
  | 'SNAPSHOT_ID_MISMATCH'
  | 'TRANSFER_DIRECTION'
  | 'TRANSFER_STATE'
  | 'CORRELATION_MISMATCH';

export class WireValidationError extends Error {
  constructor(
    public readonly code: WireValidationCode,
    message: string = code,
  ) {
    super(message);
    this.name = 'WireValidationError';
  }
}

const UUID_BYTES = 16;
const SHA256_BYTES = 32;
const MAX_FILE_CHUNK_BYTES = 16 * 1024;
const MAX_QUERY_PAGE_SIZE = 100;
const MAX_QUERY_CURSOR_BYTES = 1_024;
const CLIENT_STATUSES = new Set([
  'LEAD',
  'PRE_QUALIFIED',
  'ACTIVE',
  'PROCESSING',
  'UNDERWRITING',
  'CLEAR_TO_CLOSE',
  'CLOSED',
  'DENIED',
  'INACTIVE',
]);

type Oneof = { case: string; value: unknown } | { case: undefined; value?: undefined };

function fail(code: WireValidationCode, message: string): never {
  throw new WireValidationError(code, message);
}

function requireOneof(oneof: Oneof, field: string): { case: string; value: unknown } {
  if (oneof.case === undefined || oneof.value === undefined) {
    fail('MISSING_ONEOF', `${field} must contain exactly one alternative`);
  }
  return oneof;
}

function requireBytes(value: Uint8Array, field: string, expectedLength?: number): void {
  if (
    value.byteLength === 0 ||
    (expectedLength !== undefined && value.byteLength !== expectedLength)
  ) {
    fail(
      'INVALID_FIELD',
      expectedLength === undefined
        ? `${field} must be non-empty`
        : `${field} must contain exactly ${expectedLength} bytes`,
    );
  }
}

function requireUuid(value: Uint8Array, field: string): void {
  requireBytes(value, field, UUID_BYTES);
}

function requireSha256(value: Uint8Array, field: string): void {
  requireBytes(value, field, SHA256_BYTES);
}

function sameBytes(left: Uint8Array, right: Uint8Array): boolean {
  return (
    left.byteLength === right.byteLength && left.every((value, index) => value === right[index])
  );
}

function requireSameBytes(
  left: Uint8Array,
  right: Uint8Array,
  field: string,
  code: WireValidationCode = 'CORRELATION_MISMATCH',
): void {
  if (!sameBytes(left, right)) fail(code, `${field} does not match its bound identifier`);
}

function requireCursorPair(terminal: boolean, nextCursor: Uint8Array, field: string): void {
  if (terminal && nextCursor.byteLength !== 0) {
    fail('INVALID_CURSOR_STATE', `${field}.next_cursor must be empty on a terminal page`);
  }
  if (!terminal && nextCursor.byteLength === 0) {
    fail('INVALID_CURSOR_STATE', `${field}.next_cursor is required on a non-terminal page`);
  }
}

export function validateResponse(response: ResponseV1): void {
  requireUuid(response.requestId, 'response.request_id');
  const outcome = requireOneof(response.outcome, 'response.outcome');
  if (outcome.case === 'error') {
    const error = outcome.value as StableErrorV1;
    if (error.code === ErrorCodeV1.UNSPECIFIED || error.safeMessage.trim() === '') {
      fail('INVALID_FIELD', 'response.error must contain a stable code and safe message');
    }
    if (error.correlationId.trim() === '') {
      fail('INVALID_FIELD', 'response.error.correlation_id must be non-empty');
    }
  }
  if (outcome.case === 'changePage') validateChangePage(outcome.value as ChangePageV1);
  if (outcome.case === 'clientPage')
    validatePageCursor(outcome.value as ClientPageV1, 'response.client_page');
  if (outcome.case === 'documentPage')
    validatePageCursor(outcome.value as DocumentPageV1, 'response.document_page');
  if (outcome.case === 'conflict') validateConflict(outcome.value as ConflictV1);
}

function validateConflict(conflict: ConflictV1): void {
  requireUuid(conflict.entityId, 'response.conflict.entity_id');
  requireUuid(conflict.rejectedOperationId, 'response.conflict.rejected_operation_id');
  if (conflict.currentRevision < 1n) {
    fail('INVALID_FIELD', 'response.conflict.current_revision must be positive');
  }
  const representation = requireOneof(conflict.representation, 'response.conflict.representation');
  if (representation.case === 'client') {
    const client = representation.value as { clientId: Uint8Array; revision: bigint };
    requireSameBytes(client.clientId, conflict.entityId, 'response.conflict.client_id');
    if (client.revision !== conflict.currentRevision)
      fail(
        'CORRELATION_MISMATCH',
        'response.conflict.client_revision does not match current_revision',
      );
  } else if (representation.case === 'document') {
    const document = representation.value as { documentId: Uint8Array; revision: bigint };
    requireSameBytes(document.documentId, conflict.entityId, 'response.conflict.document_id');
    if (document.revision !== conflict.currentRevision)
      fail(
        'CORRELATION_MISMATCH',
        'response.conflict.document_revision does not match current_revision',
      );
  } else {
    fail('MISSING_ONEOF', 'response.conflict.representation must contain a typed snapshot');
  }
}

export function validateEnvelope(envelope: EnvelopeV1): void {
  requireUuid(envelope.messageId, 'envelope.message_id');
  const payload = requireOneof(envelope.payload, 'envelope.payload');
  switch (payload.case) {
    case 'response': {
      const response = payload.value as ResponseV1;
      validateResponse(response);
      requireSameBytes(response.requestId, envelope.messageId, 'response.request_id');
      break;
    }
    case 'request':
      validateCommandRequest(payload.value as CommandRequestV1);
      break;
    case 'query':
      validateQueryRequest(payload.value as QueryRequestV1);
      break;
    case 'committedEvent':
      validateCommittedEvent(payload.value as CommittedEventV1);
      break;
    case 'snapshot':
      validateSnapshotPage(payload.value as SnapshotPageV1);
      break;
    case 'snapshotControl':
      validateSnapshotControl(payload.value as SnapshotControlV1);
      break;
    case 'fileTransfer':
      validateFileTransferShape(payload.value as FileTransferV1);
      break;
    case 'sessionControl':
      requireOneof((payload.value as { message: Oneof }).message, 'session_control.message');
      break;
    case 'presence': {
      const presence = payload.value as { sessionId: Uint8Array; entityId: Uint8Array };
      requireUuid(presence.sessionId, 'presence.session_id');
      requireUuid(presence.entityId, 'presence.entity_id');
      break;
    }
    default:
      fail('MISSING_ONEOF', `unsupported envelope payload ${(payload as { case: string }).case}`);
  }
}

export function validateCommandRequest(request: CommandRequestV1): void {
  requireUuid(request.operationId, 'request.operation_id');
  requireUuid(request.actorProfileId, 'request.actor_profile_id');
  const command = requireOneof(request.command, 'request.command');
  switch (command.case) {
    case 'createClient':
      if (
        request.entityType !== EntityTypeV1.CLIENT ||
        request.entityId.byteLength !== 0 ||
        request.baseRevision !== 0n
      ) {
        fail('CORRELATION_MISMATCH', 'create_client must target a new client at revision zero');
      }
      break;
    case 'updateClient':
    case 'archiveClient':
      if (request.entityType !== EntityTypeV1.CLIENT)
        fail('CORRELATION_MISMATCH', `${command.case} must target a client`);
      requireUuid(request.entityId, 'request.entity_id');
      if (request.baseRevision < 1n)
        fail('INVALID_FIELD', `${command.case} requires a positive base revision`);
      break;
    case 'commitDocumentUpload':
      if (request.entityType !== EntityTypeV1.DOCUMENT)
        fail('CORRELATION_MISMATCH', 'commit_document_upload must target a document');
      requireUuid(request.entityId, 'request.entity_id');
      break;
    default:
      fail('MISSING_ONEOF', `unsupported command ${(command as { case: string }).case}`);
  }
}

export function validateQueryRequest(query: QueryRequestV1): void {
  const request = requireOneof(query.query, 'query.query');
  switch (request.case) {
    case 'getClient':
      requireUuid((request.value as { clientId: Uint8Array }).clientId, 'get_client.client_id');
      break;
    case 'listDocuments':
      requireUuid((request.value as { clientId: Uint8Array }).clientId, 'list_documents.client_id');
      validateQueryPage(
        request.value as { pageSize: number; cursor: Uint8Array },
        'list_documents',
      );
      break;
    case 'prepareDocumentUpload':
      validatePrepareDocumentUpload(request.value as PrepareDocumentUploadV1);
      break;
    case 'beginDocumentDownload':
      requireUuid(
        (request.value as { fileVersionId: Uint8Array }).fileVersionId,
        'begin_document_download.file_version_id',
      );
      break;
    case 'getSnapshot':
      requireUuid(
        (request.value as { snapshotId: Uint8Array }).snapshotId,
        'get_snapshot.snapshot_id',
      );
      break;
    case 'listClients': {
      const value = request.value as { pageSize: number; cursor: Uint8Array; status: string };
      validateQueryPage(value, 'list_clients');
      if (value.status !== '' && !CLIENT_STATUSES.has(value.status)) {
        fail('INVALID_FIELD', 'list_clients.status is not a supported client status');
      }
      break;
    }
    case 'getChangesAfter':
      validateQueryPage(
        request.value as { pageSize: number; cursor: Uint8Array },
        'get_changes_after',
      );
      break;
    default:
      fail('MISSING_ONEOF', `unsupported query ${(request as { case: string }).case}`);
  }
}

function validateQueryPage(value: { pageSize: number; cursor: Uint8Array }, field: string): void {
  if (value.pageSize < 0 || value.pageSize > MAX_QUERY_PAGE_SIZE) {
    fail('INVALID_FIELD', `${field}.page_size must be between 0 and ${MAX_QUERY_PAGE_SIZE}`);
  }
  if (value.cursor.byteLength > MAX_QUERY_CURSOR_BYTES) {
    fail('INVALID_FIELD', `${field}.cursor is too large`);
  }
}

export function validatePrepareDocumentUpload(request: PrepareDocumentUploadV1): void {
  const target = requireOneof(request.target, 'prepare_document_upload.target');
  if (target.case !== 'clientId' && target.case !== 'documentId')
    fail('INVALID_FIELD', 'prepare upload target is invalid');
  requireUuid(target.value as Uint8Array, `prepare_document_upload.${target.case}`);
  requireUuid(request.operationId, 'prepare_document_upload.operation_id');
  if (request.originalFileName.trim() === '' || request.declaredMime.trim() === '') {
    fail('INVALID_FIELD', 'prepare upload filename and MIME are required');
  }
}

export function validatePrepareUploadMaterial(material: PrepareUploadMaterialV1): void {
  const target = requireOneof(material.target, 'prepare_upload_material.target');
  if (target.case !== 'clientId' && target.case !== 'documentId')
    fail('INVALID_FIELD', 'prepare upload material target is invalid');
  requireUuid(target.value as Uint8Array, `prepare_upload_material.${target.case}`);
  requireUuid(material.operationId, 'prepare_upload_material.operation_id');
  requireUuid(material.sessionId, 'prepare_upload_material.session_id');
  requireUuid(material.actorProfileId, 'prepare_upload_material.actor_profile_id');
}

export function validatePrepareUploadBinding(
  request: PrepareDocumentUploadV1,
  material: PrepareUploadMaterialV1,
): void {
  validatePrepareDocumentUpload(request);
  validatePrepareUploadMaterial(material);
  requireSameBytes(request.operationId, material.operationId, 'prepare operation_id');
  requireSameBytes(
    request.target.value as Uint8Array,
    material.target.value as Uint8Array,
    'prepare target',
  );
  if (request.target.case !== material.target.case)
    fail('CORRELATION_MISMATCH', 'prepare target type does not match material');
  if (request.baseRevision !== material.baseRevision)
    fail('CORRELATION_MISMATCH', 'prepare base_revision does not match material');
  if (
    request.displayName !== material.displayName ||
    request.category !== material.category ||
    request.notes !== material.notes ||
    request.originalFileName !== material.originalFileName ||
    request.declaredMime !== material.declaredMime ||
    request.declaredSize !== material.declaredSize
  ) {
    fail('CORRELATION_MISMATCH', 'prepare metadata does not match material');
  }
}

export function validateChangePage(page: ChangePageV1): void {
  if (page.terminal) {
    if (page.terminalCursor.byteLength === 0)
      fail('INVALID_CURSOR_STATE', 'change_page.terminal_cursor is required on a terminal page');
    if (page.nextCursor.byteLength !== 0)
      fail('INVALID_CURSOR_STATE', 'change_page.next_cursor must be empty on a terminal page');
  } else {
    if (page.nextCursor.byteLength === 0)
      fail('INVALID_CURSOR_STATE', 'change_page.next_cursor is required on a non-terminal page');
    if (page.terminalCursor.byteLength !== 0)
      fail(
        'INVALID_CURSOR_STATE',
        'change_page.terminal_cursor must be empty on a non-terminal page',
      );
  }
  page.events.forEach(validateCommittedEvent);
}

export function validatePageCursor(
  page: { terminal: boolean; nextCursor: Uint8Array },
  field = 'page',
): void {
  requireCursorPair(page.terminal, page.nextCursor, field);
}

export function validateCommittedEvent(event: CommittedEventV1): void {
  requireUuid(event.operationId, 'event.operation_id');
  requireUuid(event.entityId, 'event.entity_id');
  if (event.resultingRevision < 1n)
    fail('INVALID_FIELD', 'event.resulting_revision must be positive');
  if (event.syncCursor.byteLength === 0)
    fail('INVALID_FIELD', 'event.sync_cursor must be non-empty');
  const representation = requireOneof(event.representation, 'event.representation');
  if (event.entityType === EntityTypeV1.CLIENT && representation.case !== 'client') {
    fail('CORRELATION_MISMATCH', 'client event must carry a client representation');
  }
  if (event.entityType === EntityTypeV1.DOCUMENT && representation.case !== 'document') {
    fail('CORRELATION_MISMATCH', 'document event must carry a document representation');
  }
  if (event.entityType !== EntityTypeV1.CLIENT && event.entityType !== EntityTypeV1.DOCUMENT) {
    fail('INVALID_FIELD', 'event.entity_type must be a supported entity');
  }
  const clientEvent =
    event.eventType === EventTypeV1.CLIENT_CREATED ||
    event.eventType === EventTypeV1.CLIENT_UPDATED ||
    event.eventType === EventTypeV1.CLIENT_ARCHIVED;
  if (clientEvent && event.entityType !== EntityTypeV1.CLIENT) {
    fail('CORRELATION_MISMATCH', 'client event types must target a client');
  }
  if (
    event.eventType === EventTypeV1.DOCUMENT_COMMITTED &&
    event.entityType !== EntityTypeV1.DOCUMENT
  ) {
    fail('CORRELATION_MISMATCH', 'document event types must target a document');
  }
  const representationValue = representation.value as {
    clientId?: Uint8Array;
    documentId?: Uint8Array;
    revision: bigint;
  };
  const representationId =
    event.entityType === EntityTypeV1.CLIENT
      ? representationValue.clientId
      : representationValue.documentId;
  if (representationId === undefined)
    fail('CORRELATION_MISMATCH', 'event representation is missing its entity ID');
  requireSameBytes(representationId, event.entityId, 'event.entity_id');
  if (representationValue.revision !== event.resultingRevision)
    fail('CORRELATION_MISMATCH', 'event representation revision does not match resulting_revision');
  if (event.eventType === EventTypeV1.UNSPECIFIED)
    fail('INVALID_FIELD', 'event.event_type must be specified');
}

export function validateSnapshotControl(
  control: SnapshotControlV1,
  expectedSnapshotId?: Uint8Array,
): void {
  const message = requireOneof(control.message, 'snapshot_control.message');
  const value = message.value as {
    snapshotId: Uint8Array;
    pageSize?: number;
    terminalCursor?: Uint8Array;
  };
  requireUuid(value.snapshotId, 'snapshot.snapshot_id');
  if (expectedSnapshotId !== undefined)
    requireSameBytes(value.snapshotId, expectedSnapshotId, 'snapshot_id', 'SNAPSHOT_ID_MISMATCH');
  if (message.case === 'begin') {
    if (value.pageSize === undefined || value.pageSize < 1 || value.pageSize > 256)
      fail('INVALID_FIELD', 'snapshot begin page_size must be between 1 and 256');
  } else if (message.case === 'complete') {
    if (value.terminalCursor === undefined || value.terminalCursor.byteLength === 0)
      fail('INVALID_CURSOR_STATE', 'snapshot complete terminal_cursor is required');
  } else {
    fail('MISSING_ONEOF', `unsupported snapshot control ${(message as { case: string }).case}`);
  }
}

export function validateSnapshotPage(page: SnapshotPageV1, expectedSnapshotId?: Uint8Array): void {
  requireUuid(page.snapshotId, 'snapshot_page.snapshot_id');
  if (expectedSnapshotId !== undefined)
    requireSameBytes(page.snapshotId, expectedSnapshotId, 'snapshot_id', 'SNAPSHOT_ID_MISMATCH');
  if (page.entities.length === 0) {
    if (page.lastEntityId.byteLength !== 0 || page.lastEntityType !== EntityTypeV1.UNSPECIFIED) {
      fail('CORRELATION_MISMATCH', 'empty snapshot page cannot carry a continuation entity');
    }
    return;
  }
  requireUuid(page.lastEntityId, 'snapshot_page.last_entity_id');
  if (
    page.lastEntityType !== EntityTypeV1.CLIENT &&
    page.lastEntityType !== EntityTypeV1.DOCUMENT
  ) {
    fail(
      'INVALID_FIELD',
      'snapshot_page.last_entity_type must be specified when entities are present',
    );
  }
  page.entities.forEach(validateSnapshotEntity);
  const last = page.entities.at(-1);
  if (last === undefined || last.entity.case === undefined)
    fail('MISSING_ONEOF', 'snapshot entity is missing its entity alternative');
  const lastId =
    last.entity.case === 'client' ? last.entity.value.clientId : last.entity.value.documentId;
  const lastType = last.entity.case === 'client' ? EntityTypeV1.CLIENT : EntityTypeV1.DOCUMENT;
  requireSameBytes(lastId, page.lastEntityId, 'snapshot continuation entity');
  if (lastType !== page.lastEntityType)
    fail('CORRELATION_MISMATCH', 'snapshot continuation type does not match the final entity');
}

function validateSnapshotEntity(entity: SnapshotEntityV1): void {
  const alternative = requireOneof(entity.entity, 'snapshot_entity.entity');
  const value = alternative.value as { clientId?: Uint8Array; documentId?: Uint8Array };
  const id =
    alternative.case === 'client'
      ? value.clientId
      : alternative.case === 'document'
        ? value.documentId
        : undefined;
  if (id === undefined) fail('CORRELATION_MISMATCH', 'snapshot entity is missing its identifier');
  requireUuid(id, `snapshot_entity.${alternative.case}_id`);
}

export interface FileTransferExpectation {
  transferId: Uint8Array;
  direction: 'upload' | 'download';
  nextChunkIndex: number;
  totalChunks?: number;
  completed?: boolean;
}

export class FileTransferValidator {
  readonly #state: FileTransferExpectation;

  constructor(state: FileTransferExpectation) {
    requireUuid(state.transferId, 'transfer.transfer_id');
    if (!Number.isSafeInteger(state.nextChunkIndex) || state.nextChunkIndex < 0)
      fail('TRANSFER_STATE', 'transfer next chunk index is invalid');
    if (
      state.direction === 'download' &&
      (state.totalChunks === undefined ||
        !Number.isSafeInteger(state.totalChunks) ||
        state.totalChunks < 0)
    ) {
      fail('TRANSFER_STATE', 'download transfer total_chunks is required');
    }
    this.#state = { ...state, transferId: state.transferId.slice() };
  }

  accept(frame: FileTransferV1): void {
    if (this.#state.completed) fail('TRANSFER_STATE', 'transfer is already complete');
    const message = requireOneof(frame.message, 'file_transfer.message');
    if (this.#state.direction === 'upload' && message.case !== 'uploadChunk')
      fail('TRANSFER_DIRECTION', 'upload transfer cannot accept a download frame');
    if (this.#state.direction === 'download' && message.case === 'uploadChunk')
      fail('TRANSFER_DIRECTION', 'download transfer cannot accept an upload frame');
    const value = message.value as {
      transferId: Uint8Array;
      index?: number;
      bytes?: Uint8Array;
      chunkSha256?: Uint8Array;
      totalChunks?: number;
      plaintextSha256?: Uint8Array;
    };
    requireUuid(value.transferId, 'file_transfer.transfer_id');
    requireSameBytes(value.transferId, this.#state.transferId, 'transfer_id', 'TRANSFER_STATE');
    if (message.case === 'downloadComplete') {
      if (this.#state.direction !== 'download')
        fail('TRANSFER_DIRECTION', 'upload transfer cannot accept download completion');
      const expectedTotalChunks = this.#state.totalChunks;
      if (expectedTotalChunks === undefined || value.totalChunks !== expectedTotalChunks)
        fail('TRANSFER_STATE', 'download completion total_chunks does not match prepared transfer');
      requireSha256(
        value.plaintextSha256 ?? new Uint8Array(),
        'download_complete.plaintext_sha256',
      );
      if (this.#state.nextChunkIndex !== expectedTotalChunks)
        fail('TRANSFER_STATE', 'download completion arrived before all chunks');
      this.#state.completed = true;
      return;
    }
    if (value.index !== this.#state.nextChunkIndex)
      fail('TRANSFER_STATE', 'file chunk index is not contiguous');
    if (
      value.bytes === undefined ||
      value.bytes.byteLength === 0 ||
      value.bytes.byteLength > MAX_FILE_CHUNK_BYTES
    )
      fail('TRANSFER_STATE', 'file chunk size is invalid');
    requireSha256(value.chunkSha256 ?? new Uint8Array(), 'file_chunk.chunk_sha256');
    this.#state.nextChunkIndex += 1;
  }

  get nextChunkIndex(): number {
    return this.#state.nextChunkIndex;
  }

  get completed(): boolean {
    return this.#state.completed === true;
  }
}

export function validateFileTransferShape(transfer: FileTransferV1): void {
  const message = requireOneof(transfer.message, 'file_transfer.message');
  const value = message.value as {
    transferId: Uint8Array;
    chunkSha256?: Uint8Array;
    plaintextSha256?: Uint8Array;
    bytes?: Uint8Array;
  };
  requireUuid(value.transferId, 'file_transfer.transfer_id');
  if (message.case === 'downloadComplete')
    requireSha256(value.plaintextSha256 ?? new Uint8Array(), 'download_complete.plaintext_sha256');
  else {
    if (
      value.bytes === undefined ||
      value.bytes.byteLength === 0 ||
      value.bytes.byteLength > MAX_FILE_CHUNK_BYTES
    )
      fail('TRANSFER_STATE', 'file chunk size is invalid');
    requireSha256(value.chunkSha256 ?? new Uint8Array(), 'file_chunk.chunk_sha256');
  }
}
